import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

/* Material components */
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTableModule } from '@angular/material/table';

/* KendoUI DropDownsModule */
import { DropDownsModule, ComboBoxModule, DropDownListModule, AutoCompleteModule } from '@progress/kendo-angular-dropdowns';

import { ChartsModule } from '@progress/kendo-angular-charts';

/* directives */
import { DigitOnlyDirective } from './digit-only/digit-only.directive';

/* Custom components */
import { GravatarComponent } from './gravatar/gravatar.component';

import { DetailsToolbarComponent } from './details-toolbar/details-toolbar.component';
import { DetailsToolbarBasicInfoComponent } from './details-toolbar/basic-info/basic-info.component';
import { DetailsToolbarStatusComponent } from './details-toolbar/status/status.component';
import { DetailsToolbarAdditionalInfoComponent } from './details-toolbar/additional-info/additional-info.component';
import { DetailsToolbarActionsComponent } from './details-toolbar/actions/actions.component';

import { BusinessDomainIconComponent } from './business-domain/business-domain-icon.component';
import { BusinessDomainSelectComponent } from './business-domain/business-domain-select.component';
import { BusinessDomainService } from './business-domain/business-domain.service';

import { SchedulingStatusChartComponent } from './scheduling-status/scheduling-status-chart.component';
import { SchedulingStatusIconComponent } from './scheduling-status/scheduling-status-icon.component';
import { SchedulingStatusService } from './scheduling-status/scheduling-status.service';

import { TaskTypeSelectComponent } from './task-type/task-type-select.component';
import { TaskTypeService } from './task-type/task-type.service';
import { InventoryService } from 'src/app/product-extensions/components/inventory/inventory.service';

import { BlahModule } from '@headpower/blah-ng';
import { RouterModule } from '@angular/router';
import { HpoIconModule, ButtonModule, SelectModule, InputModule, PlaceholderModule, SpinnerModule, HpoSplitButtonModule, TextTruncatePipeModule } from '@headpower/components';
// Dialogs
import { DeleteConfirmationDialogComponent } from './generic-dialogs/delete-confirmation-dialog/delete-confirmation-dialog.component';
import { DoubleConfirmationDialogComponent } from './generic-dialogs/double-confirmation-dialog/double-confirmation-dialog.component';
import { ChangeConfirmationDialogComponent } from './generic-dialogs/change-confirmation-dialog/change-confirmation-dialog.component';
import { TaskUnsavedChangesDialogComponent } from './unsaved-changes-dialog/task-unsaved-changes-dialog.component';
import { ConstructionUnitDialogComponent } from './construction-unit-dialog/construction-unit-dialog.component';
import { NotificationDialogComponent } from './generic-dialogs/notification-dialog/notification-dialog.component';
import { ListSearchComponent } from './list-search/list-search.component';
import { SharedPipeModule } from '../pipes/shared-pipe.module';
import { CostChartComponent } from './cost-chart/cost-chart.component';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { PopupModule } from '@progress/kendo-angular-popup';
import { GridMenuTemplateComponent } from './grid-menu-template/grid-menu-template.component';
import { ColumnChooserComponent } from './column-chooser/column-chooser.component';
import { NameConfirmDialogComponent } from './name-confirm-dialog/name-confirm-dialog.component';
import { WarningComponent } from './warning/warning.component';
import { ExternalRequestService } from './external-request/external-request.service';
import { ExternalUnitImportDialogComponent } from './external-request/external-unit-import/external-unit-import-dialog.component';
import { ExternalAreaImportDialogComponent } from './external-request/external-area-import/external-area-import-dialog.component';
import { PermitRequestDialogComponent } from './external-request/permit-request/permit-request-dialog.component';
import { SelectListService } from '../settings/components/common/select-list-management/select-lists.service';
import { AddItemsToTenderingDialogComponent } from './add-items-to-tendering/add-items-to-tendering-dialog.component';
import { AddItemsToTenderingService } from './add-items-to-tendering/add-items-to-tendering.service';
import { ShowPicturesDialogComponent } from './generic-dialogs/show-pictures-dialog/show-pictures-dialog.component';
import { PrintPdfDialogComponent } from './print-pdf/print-pdf-dialog.component';
import { DetailsToolbarCreatedAndModifiedInfoComponent } from './details-toolbar/created-and-changed-info/created-and-changed-info.component';
import { MemoComponent } from './memo/memo.component';
import { MemoDialogComponent } from './memo/memo-dialog/memo-dialog.component';
import { AttachmentsMaxFileSizeComponent } from './attachments-max-file-size/attachments-max-file-size.component';
import { SaveButtonComponent } from './save-button/save-button.component';
import { UnitsDashboardComponent } from './units-dashboard/units-dashboard.component';
import { PriceListAutoCompleteComponent } from './price-list-auto-complete/price-list-auto-complete.component';
import { BlobImageDirective } from './directives/blob-image.directive';
import { FavoriteStarComponent } from './favorite-star/favorite-star.component';

@NgModule({
    declarations: [
        DigitOnlyDirective,
        BusinessDomainIconComponent,
        BusinessDomainSelectComponent,
        SchedulingStatusChartComponent,
        SchedulingStatusIconComponent,
        TaskTypeSelectComponent,
        GravatarComponent,
        DetailsToolbarComponent,
        DetailsToolbarBasicInfoComponent,
        DetailsToolbarStatusComponent,
        DetailsToolbarAdditionalInfoComponent,
        DetailsToolbarActionsComponent,
        DeleteConfirmationDialogComponent,
        DoubleConfirmationDialogComponent,
        ChangeConfirmationDialogComponent,
        TaskUnsavedChangesDialogComponent,
        NotificationDialogComponent,
        ListSearchComponent,
        ConstructionUnitDialogComponent,
        CostChartComponent,
        ColumnChooserComponent,
        NameConfirmDialogComponent,
        GridMenuTemplateComponent,
        WarningComponent,
        ExternalUnitImportDialogComponent,
        ExternalAreaImportDialogComponent,
        PermitRequestDialogComponent,
        AddItemsToTenderingDialogComponent,
        ShowPicturesDialogComponent,
        PrintPdfDialogComponent,
        DetailsToolbarCreatedAndModifiedInfoComponent,
        MemoComponent,
        MemoDialogComponent,
        SaveButtonComponent,
        AttachmentsMaxFileSizeComponent,
        UnitsDashboardComponent,
        PriceListAutoCompleteComponent,
        FavoriteStarComponent
    ],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatMenuModule,
        MatIconModule,
        MatButtonModule,
        MatInputModule,
        MatSelectModule,
        MatDialogModule,
        MatTooltipModule,
        BlahModule,
        RouterModule,
        ComboBoxModule,
        DropDownListModule,
        AutoCompleteModule,
        HpoIconModule,
        ButtonModule,
        MatSlideToggleModule,
        MatBadgeModule,
        SharedPipeModule,
        SelectModule,
        MatCheckboxModule,
        DropDownsModule,
        DateInputsModule,
        PopupModule,
        InputModule,
        PlaceholderModule,
        MatAutocompleteModule,
        SpinnerModule,
        HpoSplitButtonModule,
        ChartsModule,
        MatTableModule,
        TextTruncatePipeModule,
        BlobImageDirective
    ],
    exports: [
        DigitOnlyDirective,
        BusinessDomainIconComponent,
        BusinessDomainSelectComponent,
        SchedulingStatusChartComponent,
        SchedulingStatusIconComponent,
        TaskTypeSelectComponent,
        GravatarComponent,
        DetailsToolbarComponent,
        DetailsToolbarBasicInfoComponent,
        DetailsToolbarStatusComponent,
        DetailsToolbarAdditionalInfoComponent,
        DetailsToolbarActionsComponent,
        MatSnackBarModule,
        MatIconModule,
        MemoComponent,
        SaveButtonComponent,
        AttachmentsMaxFileSizeComponent,
        // Dialogs
        DeleteConfirmationDialogComponent,
        DoubleConfirmationDialogComponent,
        ChangeConfirmationDialogComponent,
        TaskUnsavedChangesDialogComponent,
        NotificationDialogComponent,
        ListSearchComponent,
        ConstructionUnitDialogComponent,
        CostChartComponent,
        ColumnChooserComponent,
        NameConfirmDialogComponent,
        GridMenuTemplateComponent,
        WarningComponent,
        ExternalUnitImportDialogComponent,
        ExternalAreaImportDialogComponent,
        AddItemsToTenderingDialogComponent,
        ShowPicturesDialogComponent,
        DetailsToolbarCreatedAndModifiedInfoComponent,
        MemoComponent,
        UnitsDashboardComponent,
        PriceListAutoCompleteComponent,
        FavoriteStarComponent
    ],
    providers: [
        BusinessDomainService,
        SchedulingStatusService,
        TaskTypeService,
        SelectListService,
        AddItemsToTenderingService,
        ExternalRequestService,
        InventoryService
    ],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA
    ]
})
export class SharedModule { }
